import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import { EditorShell } from './RichTextEditor.styles';
import { escapeSunEditorClipping } from './escapeSunEditorClipping';

/**
 * Toolbar grouped like TinyMCE (Edit | Format | Insert | View).
 * SunEditor separates groups with adjacent arrays (not '|'); '/' is a line break.
 * Video insert is intentionally omitted.
 */
export const DEFAULT_BUTTON_LIST = [
  ['undo', 'redo'],
  ['font', 'fontSize', 'formatBlock'],
  ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
  ['fontColor', 'hiliteColor', 'removeFormat'],
  ['outdent', 'indent', 'align', 'list', 'lineHeight', 'horizontalRule'],
  ['table', 'link', 'image'],
  ['fullScreen', 'showBlocks', 'codeView', 'preview'],
];

/** Drop invalid TinyMCE-style '|' separators; SunEditor uses array groups instead. */
const sanitizeButtonList = (list) => {
  if (!Array.isArray(list)) return DEFAULT_BUTTON_LIST;
  const cleaned = list
    .map((group) => {
      if (group === '/') return '/';
      if (!Array.isArray(group)) return null;
      const buttons = group.filter((btn) => btn && btn !== '|');
      return buttons.length ? buttons : null;
    })
    .filter(Boolean);
  return cleaned.length ? cleaned : DEFAULT_BUTTON_LIST;
};

/**
 * SunEditor only accepts px/%/auto heights. Callers pass bare numbers
 * ('200', 500 — TinyMCE style), which would be invalid CSS and collapse
 * the editor to min-height, so normalise them to px.
 */
const normalizeHeight = (height) => {
  if (height === undefined || height === null || height === '') return '500px';
  if (typeof height === 'number') return `${height}px`;
  return /^\d+$/.test(height) ? `${height}px` : height;
};

const DEFAULT_FONTS = [
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Impact',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

/**
 * Reusable rich-text editor (SunEditor) replacing TinyMCE.
 * Keeps the previous API: value, handleChange, height.
 */
const RichTextEditor = ({
  value,
  handleChange,
  height,
  placeholder,
  disabled,
  buttonList,
}) => {
  const editorRef = useRef(null);
  const lastExternalValue = useRef(value || '');
  const escapeCleanupRef = useRef(null);

  const getSunEditorInstance = (sunEditor) => {
    editorRef.current = sunEditor;
    if (escapeCleanupRef.current) {
      escapeCleanupRef.current();
      escapeCleanupRef.current = null;
    }
    // Portal menus/dialogs/controllers out of overflow:auto|hidden ancestors
    // (modals, scroll bodies) so they are not clipped.
    escapeCleanupRef.current = escapeSunEditorClipping(sunEditor);
  };

  useEffect(
    () => () => {
      if (escapeCleanupRef.current) {
        escapeCleanupRef.current();
        escapeCleanupRef.current = null;
      }
    },
    []
  );

  // Sync when parent changes value from outside (e.g. load / reset), without fighting typing.
  useEffect(() => {
    const next = value || '';
    if (next === lastExternalValue.current) return;
    lastExternalValue.current = next;
    if (editorRef.current) {
      editorRef.current.setContents(next);
    }
  }, [value]);

  // Insert selected image files as data URLs (parity with TinyMCE file_picker_callback).
  const handleImageUploadBefore = (files, _info, uploadHandler) => {
    const file = files && files[0];
    if (!file) {
      uploadHandler();
      return undefined;
    }
    const reader = new FileReader();
    reader.onload = () => {
      uploadHandler({
        result: [
          {
            url: reader.result,
            name: file.name,
            size: file.size,
          },
        ],
      });
    };
    reader.onerror = () => uploadHandler();
    reader.readAsDataURL(file);
    return undefined;
  };

  return (
    <EditorShell>
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        defaultValue={value || ''}
        width="100%"
        // Native TinyMCE content_style parity: applied inline on the
        // editable root, so it survives node cloning/paste better than CSS.
        setDefaultStyle="font-family: Helvetica, Arial, sans-serif; font-size: 14px;"
        onChange={(content) => {
          lastExternalValue.current = content;
          if (handleChange) handleChange(content);
        }}
        disable={!!disabled}
        setOptions={{
          height: normalizeHeight(height),
          minHeight: '160px',
          resizingBar: true,
          // Viewport-sized dialog backdrop; absolute 'local' clips inside short/overflow parents.
          popupDisplay: 'full',
          placeholder: placeholder ?? 'Start typing...',
          defaultTag: 'p',
          font: DEFAULT_FONTS,
          buttonList: sanitizeButtonList(buttonList || DEFAULT_BUTTON_LIST),
          formats: [
            'p',
            'div',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'blockquote',
            'pre',
          ],
          imageFileInput: true,
          imageUrlInput: true,
          videoFileInput: false,
          attributesWhitelist: {
            all: 'style|data-.+',
          },
        }}
        onImageUploadBefore={handleImageUploadBefore}
      />
    </EditorShell>
  );
};

RichTextEditor.defaultProps = {
  value: '',
  height: '500',
  placeholder: 'Start typing...',
  disabled: false,
  buttonList: null,
  handleChange: () => {},
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  handleChange: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  buttonList: PropTypes.array,
};

export default RichTextEditor;
